import {createContext, render} from 'preact';
import $ from '@modules/jquery-import';
import PrimaryNav from '@components/navigation/desktop';
import SiteMapService from '@services/SiteMapService';
import propEq from 'ramda/src/propEq';
import find from 'ramda/src/find';
import {getPageRoot} from '@util/URLUtils';
import {jqueryValidate, MenuAim, Waypoints} from '@modules/lazy-imports';
import {$BODY, $CONTENT_PANE, $HEADER} from '@config/selectors';

const $NAV_SITE = '#nav-lvl-site';
const $NAV_SECONDARY = '#page-secondary-nav';
const $DROP_MENU = '.nav-drop-menu';
const $NAV_MENU = '.nav-menu';
const $NAV_ITEM = '.nav-item--with-indicator';
const $MENU_TRIGGER = '[data-menu-trigger]';
const $CART_PANEL = '#cart-panel';

const getSelectedTaxonomy = ( pageRoot, taxonomyList ) => find( propEq( 'url', pageRoot ), taxonomyList );
const FORMULARY_NODE_ID = '826e8811-14fa-4b98-a383-172876e9528e';

export const NavigationContext = createContext( {
    isShoppingTaxonomy: false,
    hasFormulary: false,
    pastPurchases: []
} );

class DesktopMenu {

    _menuHoverTimeoutId = null;
    _frozenMenus = [];

    constructor ( $container ) {
        console.warn( '[Init Desktop Header]' );
        this.navPrimary = $container;

        this.initMenu();
    }

    async initMenu () {

        /*const [taxonomyData, pastPurchaseData] = await Promise.all( [
            SiteMapService.getTaxonomy(),
            // todo -> swap out with "recently viewed" data
            OrdersService.getPastPurchases( 3 )
        ] );*/
        const taxonomyData = await SiteMapService.getTaxonomy();
        const shoppingTaxonomyId = taxonomyData[0].sysId;
        const pageRoot = getPageRoot( window.location.pathname );
        const selectedTaxonomy = getSelectedTaxonomy( pageRoot, taxonomyData ) || taxonomyData[0];

        // note: we look for the presence of FORMULARY_NODE_ID on first child node to determine if a formulary was returned as
        // a navigation node for the current user context; 
        const hasFormulary = selectedTaxonomy.children[0]?.sysId === FORMULARY_NODE_ID;

        // prep navigation for client-side injection
        this.navPrimary.empty();
        this.navPrimary.removeClass( 'overflow-hidden' );

        render(
            <NavigationContext.Provider
                value={{
                    isShoppingTaxonomy: selectedTaxonomy.sysId === shoppingTaxonomyId,
                    hasFormulary: hasFormulary,
                    // todo -> swap out with "recently viewed" data
                    // pastPurchases: pastPurchaseData.slice( 0, 3 ),
                    pastPurchases: []
                }}
            >
                <PrimaryNav taxonomy={selectedTaxonomy}/>
            </NavigationContext.Provider>,
            this.navPrimary[0]
        );

        this.siteHeader = $( $HEADER );
        this.siteBody = $( $BODY );
        this.contentPane = $( $CONTENT_PANE );
        this.navSite = $( $NAV_SITE );
        this.navItems = $( $NAV_ITEM ).parent( 'li' );
        this.dropMenus = $( $DROP_MENU );
        this.submenuAds = $( `${$DROP_MENU} .submenu-figure` );
        const loginForm = $( '#loginForm1' );

        if ( loginForm[0] ) {
            jqueryValidate().then( () => loginForm.validate() );
        }

        // toggle collapsable header
        this.initCollapsingHeader();
        this.initEvents();
    }

    async initEvents () {
        await MenuAim();

        this.siteHeader
            .on( 'focusin', `${$MENU_TRIGGER} input`, this.onFocusin )
            .on( 'touchstart', $MENU_TRIGGER, this.toggleMenu )
            .on( 'click', 'a[href*="#"]', this.onInPageNavigation )

        $( '.nav-group-primary, .top-nav--desktop' ).menuAim( {
            rowSelector: `${$MENU_TRIGGER}`,
            activate: this.onDropMenuEnter,
            deactivate: this.onDropMenuLeave,
            submenuDirection: 'below',
            exitMenu: () => true
        } );

        const loginForm = $( '#loginForm1' );

        if ( loginForm[0] ) {
            jqueryValidate().then( () => loginForm.validate() );
        }

        // detect click events outside a menu to thaw/deactivate all menus
        $( document ).on( 'click touchstart', e => {
            if ( !this.getClosestMenu( e.target ).length ) {
                this.thawAllMenus();
            }
        } );

        // allow user to open submenus (without navigating) if using touch
        $( '.nav-drop-menu' ).on( 'touchstart', e => window.USER_IS_TOUCHING && e.stopPropagation() );

        // menu-aim
        this.dropMenus.find( $NAV_MENU ).menuAim( {
            rowSelector: '> ul > li',
            activate: this.activateMenuItem,
            deactivate: this.deactivateMenuItem,
            // note -> "exitMenu => true" required to resolve last active not reactivated edge-case
            // https://github.com/kamens/jQuery-menu-aim/issues/14#issuecomment-17252987
            exitMenu: () => true
        } );

    }

    onInPageNavigation = e => {
        const activeMenu = $( e.currentTarget ).parents( ['data-state="active"'] );
        this.deactivateMenu( activeMenu );
    };

    onDropMenuEnter = el => {
        this._menuHoverTimeoutId = setTimeout( () => this.activateMenu( el ), 150 );
    };

    onDropMenuLeave = el => {
        if ( this._frozenMenus.length ) {
            return;
        }

        clearTimeout( this._menuHoverTimeoutId );
        this.deactivateMenu( el );
    };

    onFocusin = e => {
        const menu = this.getClosestMenu( e.target );
        this.freezeMenu( menu );
    };

    toggleMenu = e => {
        const el = $( e.currentTarget );

        (el[0].dataset['state'] === 'active')
            ? this.deactivateMenu( el )
            : this.activateMenu( el );
    };

    getClosestMenu = target => $( target ).closest( $MENU_TRIGGER );

    freezeMenu = ( menu ) => {
        this._frozenMenus = this._frozenMenus.concat( [menu] );
    };

    thawAllMenus = () => {
        this._frozenMenus.forEach( menu => {
            menu.find( 'input' ).blur();
            this.deactivateMenu( menu );
        } );

        this._frozenMenus = [];
    };

    activateMenu = ( el ) => {
        const $el = $( el )
        if ( $el[0].dataset['state'] === 'active' ) {
            return;
        }

        this.thawAllMenus();
        $el.attr( 'data-state', 'active' );
        this.contentPane.attr( 'data-overlay', 'active' );
        $el.find( $DROP_MENU ).removeClass( 'hidden' );
        $el.find( $DROP_MENU ).addClass( 'flex' );
    };

    deactivateMenu = el => {
        const $el = $( el );

        this.resetActive();
        $el.removeAttr( 'data-state' );
        this.contentPane.attr( 'data-overlay', 'inactive' );
        $el.find( $DROP_MENU ).addClass( 'hidden' );
        $el.find( $DROP_MENU ).removeClass( 'flex' );
    };

    activateMenuItem = el => {
        const menuId = el.getAttribute( 'data-menu-id' );
        this.getMenuFigure( menuId ).removeClass( 'hidden' );

        $( el ).attr( 'data-state', 'active' );
    };

    deactivateMenuItem = el => {
        const menuId = el.getAttribute( 'data-menu-id' );
        this.getMenuFigure( menuId ).addClass( 'hidden' );

        $( el ).removeAttr( 'data-state' );
    };

    resetActive = () => {
        // hide ad placements & toggle active menuItem in menu
        this.submenuAds.addClass( 'hidden' );
        this.navItems.removeAttr( 'data-state' );
    };

    getMenuFigure = id => {
        return this.submenuAds.filter( ( i, el ) => el.getAttribute( 'data-ad-id' ) === id );
    };

    shouldCollapseHeader () {
        return document.documentElement.scrollHeight - window.innerHeight > this.siteHeader.height();
    }

    async initCollapsingHeader () {
        await Waypoints();

        const offsetHeightFull = this.siteHeader.height();
        const $cartPanel = $( $CART_PANEL );
        const $primaryNav = $( $NAV_SITE );
        const $headerFixedSpacer = $( `<div id="header-fixed-spacer" class="hidden lg:block pointer-events-none"></div>` );

        // spacer used to control scroll jumping when site header is set to fixed position
        $( '#header-outer' ).append( $headerFixedSpacer )

        new Waypoint( {
            element: upi.conf.SCROLL_CONTEXT,
            handler: dir => {
                const isDownDir = dir === 'down';
                if ( this.shouldCollapseHeader() ) {
                    this.siteBody.toggleClass( 'header--collapsed', isDownDir );
                    $headerFixedSpacer.height( isDownDir ? offsetHeightFull : 0 );

                    if ( isDownDir ) {
                        $cartPanel.css( {
                            position: 'fixed',
                            top: $primaryNav.height() + ($( $NAV_SECONDARY ).height() || 0)
                        } )
                    }
                    else {
                        $cartPanel.css( {position: 'absolute', top: 0} )
                    }
                }
            },
            offset: -this.siteHeader.height() + $primaryNav.height()
        } );

    }
}

export default DesktopMenu;
